import { ErrorBoundary } from "react-error-boundary";
import { useState, useEffect, Suspense, lazy } from "react";
import {
  getRoutePath,
  RouterProvider,
  shouldScrollToRouteTop,
  useLocation,
  useScrollTarget,
} from "@/lib/simple-router";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Navigation from "@/components/navigation";
// OLD GA4 direct tracking - disabled, now using GTM (GTM-TTMK83QL)
// import { initGA } from "./lib/analytics";
// import { useAnalytics } from "./hooks/use-analytics";
import Home from "@/pages/home";
import PropertyPage from "@/pages/property";
import NotFound from "@/pages/not-found";

const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const BookingPage = lazy(() => import("@/pages/booking"));
const Checkout = lazy(() => import("@/pages/checkout"));
const BookingConfirmation = lazy(() => import("@/pages/booking-confirmation"));
const AdminPage = lazy(() => import("@/pages/admin"));
const AdminPhotoCategorization = lazy(() => import("@/pages/admin-photo-categorization"));
const AdminAmenityPhotos = lazy(() => import("@/pages/admin-amenity-photos"));
const AdminBedPhotos = lazy(() => import("@/pages/admin-bed-photos"));
const AdminBlog = lazy(() => import("@/pages/admin-blog"));
const AdminPromoCodes = lazy(() => import("@/pages/admin-promo-codes"));
const AdminLogin = lazy(() => import("@/pages/admin-login"));
const TermsPage = lazy(() => import("@/pages/terms"));
const ContactThankYou = lazy(() => import("@/pages/contact-thank-you"));
const Villas = lazy(() => import("@/pages/villas"));
const AmenitiesExperience = lazy(() => import("@/pages/amenities-experience"));
const EventsWeddings = lazy(() => import("@/pages/events-weddings"));
const GuestInfo = lazy(() => import("@/pages/guest-info"));
const Contact = lazy(() => import("@/pages/contact"));

const pageMetadata: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Toka Ridge - Luxury Lake Accommodation | Rotorua, New Zealand',
    description: 'Experience luxury lakefront accommodation at Toka Ridge, Rotorua. Five stunning properties with breathtaking lake views, hot tubs, and premium amenities.',
  },
  '/villas': {
    title: 'Luxury Villas in Rotorua | Toka Ridge',
    description: 'Explore Toka Ridge luxury villas overlooking Lake Rotorua, designed for memorable group stays, family escapes and special occasions.',
  },
  '/amenities-experience': {
    title: 'Amenities & Experiences | Toka Ridge Rotorua',
    description: 'Discover the premium amenities and thoughtful experiences included with a stay at Toka Ridge on the shores of Lake Rotorua.',
  },
  '/events-weddings': {
    title: 'Rotorua Events & Weddings | Toka Ridge',
    description: 'Host an intimate wedding, celebration or private event in a remarkable Lake Rotorua setting at Toka Ridge.',
  },
  '/guest-info': {
    title: 'Guest Information | Toka Ridge Rotorua',
    description: 'Plan your Toka Ridge stay with useful arrival, accommodation and local information for guests visiting Rotorua.',
  },
  '/contact': {
    title: 'Contact Toka Ridge | Luxury Rotorua Accommodation',
    description: 'Contact the Toka Ridge team about luxury accommodation, group stays, events and weddings in Rotorua, New Zealand.',
  },
  '/blog': {
    title: 'Toka Ridge Journal | Rotorua Travel & Stay Ideas',
    description: 'Read Toka Ridge stories, Rotorua travel inspiration and ideas for making the most of your Lake Rotorua stay.',
  },
  '/terms': {
    title: 'Terms, Conditions & Privacy | Toka Ridge',
    description: 'Read the booking terms, conditions and privacy information for Toka Ridge accommodation in Rotorua.',
  },
};

const defaultMetadata = {
  title: 'Toka Ridge | Luxury Lake Accommodation in Rotorua',
  description: 'Discover luxury lake accommodation and memorable stays at Toka Ridge in Rotorua, New Zealand.',
};

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-foreground">Loading...</div>
    </div>
  );
}

function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    fetch("/api/admin/session")
      .then(res => res.json())
      .then(data => {
        setIsAuthenticated(data.isAdmin === true);
        setIsChecking(false);
        if (!data.isAdmin) {
          window.location.href = "/admin-login";
        }
      })
      .catch(() => {
        setIsChecking(false);
        window.location.href = "/admin-login";
      });
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full bg-background shadow-lg rounded-lg p-6 border border-border">
        <h2 className="text-xl font-semibold text-foreground mb-4">Something went wrong</h2>
        <p className="text-foreground mb-4">
          We encountered an unexpected error. Please try refreshing the page.
        </p>
        <button
          onClick={resetErrorBoundary}
          className="w-full bg-toka-primary hover:bg-toka-primary/90 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function SimpleRouter() {
  // Page view tracking now handled by Google Tag Manager (GTM-TTMK83QL)
  // useAnalytics();
  
  const [location] = useLocation();
  const [scrollTarget] = useScrollTarget();
  const currentPath = getRoutePath(location);

  useEffect(() => {
    if (shouldScrollToRouteTop(scrollTarget)) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentPath]);

  // Dynamic canonical tag management for SEO
  useEffect(() => {
    const baseUrl = 'https://tokaridge.co.nz';
    // Normalize path: remove trailing slash (except for homepage) for consistency
    let normalizedPath = currentPath || '/';
    if (normalizedPath !== '/' && normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }
    const canonicalUrl = normalizedPath === '/' ? baseUrl + '/' : baseUrl + normalizedPath;
    
    // Update canonical link tag (exists in index.html with id for easy selection)
    const canonicalLink = document.getElementById('canonical-link') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    }
    
    // Also update og:url meta tag for social sharing
    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (ogUrl) {
      ogUrl.content = canonicalUrl;
    }

    const metadata = pageMetadata[currentPath]
      ?? (currentPath.startsWith('/blog/') ? pageMetadata['/blog'] : defaultMetadata);
    document.title = metadata.title;
    const selectors = [
      ['meta[name="description"]', metadata.description],
      ['meta[property="og:title"]', metadata.title],
      ['meta[property="og:description"]', metadata.description],
      ['meta[name="twitter:title"]', metadata.title],
      ['meta[name="twitter:description"]', metadata.description],
    ] as const;
    selectors.forEach(([selector, content]) => {
      const element = document.querySelector(selector) as HTMLMetaElement | null;
      if (element) element.content = content;
    });
  }, [currentPath]);

  const renderPage = () => {
    try {
      if (currentPath === '/' || currentPath === '') {
        return <Home />;
      } else if (currentPath === '/villas') {
        return <Suspense fallback={<PageLoader />}><Villas /></Suspense>;
      } else if (currentPath === '/amenities-experience') {
        return <Suspense fallback={<PageLoader />}><AmenitiesExperience /></Suspense>;
      } else if (currentPath === '/events-weddings') {
        return <Suspense fallback={<PageLoader />}><EventsWeddings /></Suspense>;
      } else if (currentPath === '/guest-info') {
        return <Suspense fallback={<PageLoader />}><GuestInfo /></Suspense>;
      } else if (currentPath === '/contact') {
        return <Suspense fallback={<PageLoader />}><Contact /></Suspense>;
      } else if (currentPath === '/blog') {
        return <Suspense fallback={<PageLoader />}><Blog /></Suspense>;
      } else if (currentPath && currentPath.startsWith('/blog/')) {
        return <Suspense fallback={<PageLoader />}><BlogPost /></Suspense>;
      } else if (currentPath && currentPath.startsWith('/property/') && currentPath.endsWith('/booking')) {
        return <Suspense fallback={<PageLoader />}><BookingPage /></Suspense>;
      } else if (currentPath && currentPath.startsWith('/property/')) {
        return <PropertyPage />;
      } else if (currentPath === '/checkout') {
        return <Suspense fallback={<PageLoader />}><Checkout /></Suspense>;
      } else if (currentPath && currentPath.startsWith('/booking-confirmation/')) {
        return <Suspense fallback={<PageLoader />}><BookingConfirmation /></Suspense>;
      } else if (currentPath === '/terms') {
        return <Suspense fallback={<PageLoader />}><TermsPage /></Suspense>;
      } else if (currentPath === '/thankyouforcontact') {
        return <Suspense fallback={<PageLoader />}><ContactThankYou /></Suspense>;
      } else if (currentPath === '/admin-login') {
        return <Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>;
      } else if (currentPath === '/admin') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminPage /></Suspense></ProtectedAdminRoute>;
      } else if (currentPath === '/admin-photo-categorization') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminPhotoCategorization /></Suspense></ProtectedAdminRoute>;
      } else if (currentPath === '/admin-amenity-photos') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminAmenityPhotos /></Suspense></ProtectedAdminRoute>;
      } else if (currentPath === '/admin-bed-photos') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminBedPhotos /></Suspense></ProtectedAdminRoute>;
      } else if (currentPath === '/admin-blog') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminBlog /></Suspense></ProtectedAdminRoute>;
      } else if (currentPath === '/admin/promo-codes') {
        return <ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminPromoCodes /></Suspense></ProtectedAdminRoute>;
      } else {
        return <NotFound />;
      }
    } catch (error) {
      console.error('Route rendering error:', error);
      return <div>Page loading error. Please refresh.</div>;
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('Router error caught by boundary:', error, errorInfo);
      }}
      onReset={() => {
        window.location.reload();
      }}
    >
      <Navigation />
      <div style={{ minHeight: '100vh' }}>
        {renderPage()}
      </div>
    </ErrorBoundary>
  );
}

function App() {
  // GA4 is now managed through Google Tag Manager (GTM-TTMK83QL)
  // Direct GA4 initialization disabled to avoid double-counting
  // useEffect(() => {
  //   if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
  //     initGA();
  //   }
  // }, []);

  useEffect(() => {
    // Handle unhandled promise rejections to prevent runtime error overlay
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Get error details
      const errorMessage = event.reason?.message || '';
      const errorStack = event.reason?.stack || '';
      
      // Suppress known non-critical errors
      if (errorMessage.includes('Failed to fetch') || 
          errorMessage.includes('429') || 
          errorMessage.includes('Too Many Requests') ||
          errorMessage.includes('Network request failed') ||
          errorMessage.includes('reading \'frame\'') ||
          errorMessage.includes('scroll') ||
          errorMessage.includes('wheel') ||
          errorMessage.includes('ResizeObserver loop limit exceeded') ||
          errorMessage.includes('Non-Error promise rejection captured') ||
          errorStack.includes('ResizeObserver')) {
        // Prevent the error overlay from showing for expected errors
        event.preventDefault();
        return;
      }
      
      // Log API-related errors with more context but suppress overlay
      if (errorMessage.includes('api/') || 
          errorMessage.includes('properties') ||
          errorMessage.includes('availability') ||
          errorMessage.includes('pricing')) {
        console.warn('API-related promise rejection (suppressed):', {
          message: errorMessage,
          reason: event.reason
        });
        event.preventDefault();
        return;
      }
      
      // Log other errors but don't prevent them (they might be actual bugs)
      console.error('Unhandled promise rejection:', event.reason);
    };

    // Also handle regular JavaScript errors
    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.message || '';
      
      // Suppress ResizeObserver errors which are browser-specific and non-critical
      if (errorMessage.includes('ResizeObserver loop limit exceeded') ||
          errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('App error caught:', error, errorInfo);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider>
            <Toaster />
            <SimpleRouter />
          </RouterProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
